import { useCallback, useMemo, useState } from "react";
import { NotePreviewSection } from "../note-preview-types";

export interface UseReportSelection {
  sections: NotePreviewSection[];
  selectedSections: NotePreviewSection[];
  selectedKeys: string[];
  areAllSelected: boolean;
  isSelected: (key: string) => boolean;
  toggle: (key: string) => void;
  selectAll: () => void;
  clearAll: () => void;
}

/** Every section of the note starts in the report; the user can untick any of them or clear the lot. */
export const useReportSelection = (
  sections: NotePreviewSection[]
): UseReportSelection => {
  const [excludedKeys, setExcludedKeys] = useState<string[]>([]);

  const isSelected = useCallback(
    (key: string) => !excludedKeys.includes(key),
    [excludedKeys]
  );
  const toggle = useCallback((key: string) => {
    setExcludedKeys((prev) =>
      prev.includes(key) ? prev.filter((it) => it !== key) : [...prev, key]
    );
  }, []);
  const selectAll = useCallback(() => setExcludedKeys([]), []);
  const clearAll = useCallback(
    () => setExcludedKeys(sections.map((section) => section.key)),
    [sections]
  );
  const selectedSections = useMemo(
    () => sections.filter((section) => isSelected(section.key)),
    [sections, isSelected]
  );
  const selectedKeys = useMemo(
    () => selectedSections.map((section) => section.key),
    [selectedSections]
  );
  const areAllSelected = selectedSections.length === sections.length;

  return useMemo(
    () => ({
      sections,
      selectedSections,
      selectedKeys,
      areAllSelected,
      isSelected,
      toggle,
      selectAll,
      clearAll,
    }),
    [
      sections,
      selectedSections,
      selectedKeys,
      areAllSelected,
      isSelected,
      toggle,
      selectAll,
      clearAll,
    ]
  );
};
